/**
 * Derived fields the bundle does not carry, computed once at load.
 *
 * `unattributedLabor` is claim labor that landed on no claim: a person's labor in the
 * paid months they billed no claims (MEASURE-RULES §3.5). It is derived rather than
 * emitted by `scripts/build_bundle.py` deliberately — the month rows already ARE the
 * figure, so a second copy in the bundle would be a thing that could disagree with it.
 */
import type { CrosstabDict, Dataset, EmployeeRow, TouchMonthRow } from './types'
import { NO_CLAIM } from './noClaim'

/**
 * Whether a month row billed no claims — its labor is then unattributed, all of it.
 * Read the hydrated flag where there is one: a claim filter scales `billedClaims`, and
 * a person with none of the filtered claim type must not have every month read as
 * claimless. Rows built without `hydrate` (tests) fall back to the count.
 */
export const isNoClaimMonth = (m: Pick<TouchMonthRow, 'billedClaims' | 'noClaims'>) =>
  m.noClaims ?? m.billedClaims === 0

/**
 * Labor per person in the months they billed no claims.
 *
 * Until 2026-10-08 this was read off the crosstab's 0-claim rows. On the lakehouse the
 * allocation fact's `No Claim` labor sits ONLY in such months — $0 of it in the 2,782
 * payroll-record months that billed claims — so the two are the same pile, but the crosstab
 * counted it in months the person was not paid (the paid-month rule leaves those out),
 * in the allocation fact's dollars rather than the claim labor cost per claim divides,
 * and had no month grain, so a range could only scale it. See MEASURE-RULES §3.5.
 */
function unattributedByPerson(d: Dataset): Map<string, number> {
  const out = new Map<string, number>()
  for (const m of d.touchMonths) {
    if (!isNoClaimMonth(m)) continue
    out.set(m.employee, (out.get(m.employee) ?? 0) + m.claimLabor)
  }
  return out
}

/**
 * Gives labor on no claim its own label on a bundle built before the builder did.
 *
 * Those bundles file it under the same "Unknown" state, payer, facility group and
 * `UNKNOWN` facility as a real claim whose facility did not resolve, so the Overview
 * divided $7.1M of unattributed payroll by ~2,300 claims and drew a $1,500 "Unknown"
 * facility group. A cell on an unresolved facility with no claims in it is exactly the
 * allocation fact's `No Claim` labor, so it moves to NO_CLAIM in all four columns. A
 * cell where the two had already been summed together carries claims and stays where
 * it is — rare, and it only leaves a little labor in "Unknown".
 *
 * A bundle that already carries NO_CLAIM is returned untouched, so this is idempotent.
 */
export function splitNoClaim(ct: CrosstabDict): CrosstabDict {
  if (ct.facilities.includes(NO_CLAIM)) return ct
  const unresolved = new Set(
    ct.facilities.flatMap((f, i) => (f.trim().toUpperCase() === 'UNKNOWN' ? [i] : [])),
  )
  const moves = (r: CrosstabDict['rows'][number]) => r[6] === 0 && unresolved.has(r[5])
  if (!ct.rows.some(moves)) return ct
  const [s, p, g, f] = [ct.states, ct.payers, ct.facilityGroups, ct.facilities].map((d) => d.length)
  return {
    ...ct,
    states: [...ct.states, NO_CLAIM],
    payers: [...ct.payers, NO_CLAIM],
    facilityGroups: [...ct.facilityGroups, NO_CLAIM],
    facilities: [...ct.facilities, NO_CLAIM],
    rows: ct.rows.map((r) => (moves(r) ? [r[0], r[1], s!, p!, g!, f!, r[6], r[7]] : r)),
  }
}

/**
 * Attach the derived fields. Idempotent — it recomputes from the month rows rather
 * than reading what it wrote, so hydrating a cached (already-hydrated) dataset is safe.
 *
 * Returns a new object; the input is not mutated.
 */
export function hydrate(d: Dataset): Dataset {
  // Bundles built before the rule-of-record touch buckets carry none. Zero, not
  // undefined, so a sum stays a number and the shares read as "no data" (0 / 0).
  const touchMonths = d.touchMonths.map((t) => ({
    ...t,
    resolvedNoTouch: t.resolvedNoTouch ?? 0,
    resolvedOneTouch: t.resolvedOneTouch ?? 0,
    resolvedTwoPlusTouch: t.resolvedTwoPlusTouch ?? 0,
    resolvedTouches: t.resolvedTouches ?? 0,
    noClaims: t.billedClaims === 0,
  }))
  const unattr = unattributedByPerson({ ...d, touchMonths })
  const employees: EmployeeRow[] = d.employees.map((r) => ({
    ...r,
    // NOT floored at zero: allocations can be negative (a reversal), and flooring
    // would invent money.
    unattributedLabor: unattr.get(r.employee) ?? 0,
  }))
  return { ...d, employees, touchMonths, crosstab: splitNoClaim(d.crosstab) }
}
